import React from 'react';

import { getEmojiBasedOnTime } from '@neptune/shared/avatars-util';
import { CopyToClipboard, InputWithError, Textarea } from '@neptune/shared/common-ui';
import { OrganizationType, PrivacyAvailability } from '@neptune/shared/core-organizations-domain';
import { PROJECT_NAME_MAX_LENGTH } from '@neptune/shared/core-project-domain';
import { makeProjectIdentifier } from '@neptune/shared/core-project-util';
import { AvatarSource } from '@neptune/shared/core-users-domain';
import { bemBlock, ColorPicker, Layout, Text } from '@neptune/shared/venus-ui';

import ProjectKeyGenerator from 'components/projects/project-key-generator/ProjectKeyGenerator';
import { ProjectBeingEdited } from 'state/ui/modals/project/actions';

import { AvatarPicker } from './avatar-picker/AvatarPicker';
import { ProjectNameChangeWarning } from './ProjectNameChangeWarning';
import { ProjectVisibilitySection } from './ProjectVisibilitySection';
import withNameChecked from './withNameChecked';

import './ProjectForm.less';

const ProjectNameInput = withNameChecked(InputWithError);

export interface ProjectFormProps {
  mode: 'add' | 'edit';
  project: ProjectBeingEdited;
  organizationName?: string;
  organizationId?: string;
  organizationType: OrganizationType;
  availableProjectPrivacySettings: PrivacyAvailability[];
  projectNameError?: string;
  initialProjectName?: string;
  onProjectChange: (projectProperty: string, value: string) => void;
  onColorChange: (colorClassName: string) => void;
  onAvatarChange: (avatarUrl: string) => void;
  onProjectKeyChange: (key: string) => void;
  onNameValidationChange: (value: { error: string }) => void;
  onProjectKeyValidationChange: (value: { isProjectKeyValid: boolean }) => void;
}

const block = bemBlock('project-form');

export const ProjectForm: React.FC<ProjectFormProps> = ({
  mode,
  project,
  organizationName,
  organizationId,
  organizationType,
  availableProjectPrivacySettings,
  projectNameError,
  onProjectChange,
  onColorChange,
  onAvatarChange,
  onProjectKeyChange,
  onNameValidationChange,
  onProjectKeyValidationChange,
  initialProjectName,
}) => {
  const projectAvatar =
    project.avatarSource === AvatarSource.unicode
      ? project.avatarUrl
      : getEmojiBasedOnTime(project.timeOfCreation);

  const projectIdentifier = makeProjectIdentifier(
    organizationName || 'undefined',
    project.name || 'undefined',
  );

  const isProjectNameDirty = !!(
    initialProjectName &&
    project.name &&
    initialProjectName !== project.name &&
    !projectNameError
  );

  const handleProjectChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      onProjectChange(event.target.name, event.target.value.trimStart()),
    [onProjectChange],
  );

  return (
    <Layout.Column className={block()} spacedChildren="md" data-role="project-form">
      <Layout.Column spacedChildren="sm">
        <Layout.Column spacedChildren="xs">
          <Text fontWeight="semibold">Name</Text>
          <ProjectNameInput
            organizationName={organizationName}
            projectId={project.id}
            data-role="project-form-name"
            name="name"
            type="text"
            maxLength={PROJECT_NAME_MAX_LENGTH}
            error={projectNameError}
            value={project.name || ''}
            onValidationChange={onNameValidationChange}
            onChange={handleProjectChange}
            inputClassName={block('input')}
          />
          {mode === 'edit' && <ProjectNameChangeWarning isVisible={isProjectNameDirty} />}
        </Layout.Column>
        {mode === 'edit' && (
          <Layout.Row span="auto" alignItems="center" spacedChildren="sm">
            <Text fontWeight="semibold">Project</Text>
            <Text> = </Text>
            <Text> {projectIdentifier} </Text>
            <CopyToClipboard text={projectIdentifier} />
          </Layout.Row>
        )}
      </Layout.Column>

      <Layout.Column spacedChildren="xs">
        <Text fontWeight="semibold">Key</Text>
        <ProjectKeyGenerator
          projectKey={project.projectKey}
          projectName={project.name}
          organizationId={organizationId}
          mode={mode}
          handleProjectKeyChange={onProjectKeyChange}
          onValidationChange={onProjectKeyValidationChange}
          inputClassName={block('input')}
        />
      </Layout.Column>
      <Layout.Column spacedChildren="xs">
        <Layout.Row spacedChildren="md">
          <Layout.Column span="shrink" spacedChildren="xs">
            <Text fontWeight="semibold">Color</Text>
            <Layout.Row span="grow">
              <ColorPicker activeColor={project.displayClass} onColorChange={onColorChange} />
            </Layout.Row>
          </Layout.Column>
          <Layout.Column span="shrink" spacedChildren="xs">
            <Text fontWeight="semibold">Avatar</Text>
            <Layout.Row span="grow">
              <AvatarPicker avatar={projectAvatar} onAvatarChange={onAvatarChange} />
            </Layout.Row>
          </Layout.Column>
        </Layout.Row>
      </Layout.Column>
      <Layout.Column spacedChildren="xs">
        <Text fontWeight="semibold">Privacy</Text>
        <ProjectVisibilitySection
          visibility={project.visibility}
          organizationType={organizationType}
          availableProjectPrivacySettings={availableProjectPrivacySettings}
          onPrivacyChange={handleProjectChange}
        />
      </Layout.Column>
      <Layout.Column spacedChildren="xs">
        <Text fontWeight="semibold">Description</Text>
        <Textarea
          className={block('textarea')}
          name="description"
          value={project.description}
          onChange={handleProjectChange}
        />
      </Layout.Column>
    </Layout.Column>
  );
};
